import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar, Eye, Pencil, Download, ChevronDown, Building2, Home as HomeIcon, X, Trash2 } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { getMyReports } from "../api/myReports";
import { saveOrUpdateDailyReport } from "../api/DailyReport";
import { getAllProjectModules } from "../api/Module";
import { getAllWorkNatures } from "../api/workNature";
import { getAllClients } from "../api/client";

// ===== Style tokens — matched to Admin My Reports page =====
const colors = {
  bg: "#ffffff",
  card: "#ffffff",
  border: "#e5e7eb",
  text: "#111827",
  subtext: "#6b7280",
  heading: "#6b7280",
  primary: "#2f5fdc",
  inputBorder: "#d1d5db",
  iconGray: "#374151",
};
const styles = {
  page: {
    background: colors.bg,
    minHeight: "100vh",
    padding: "32px 40px",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  title: { fontSize: "24px", fontWeight: 700, color: colors.text, margin: 0 },
  subtitle: { fontSize: "14px", color: colors.subtext, marginTop: "4px", marginBottom: "24px" },
  filterCard: {
    background: colors.card,
    borderRadius: "12px",
    border: `1px solid ${colors.border}`,
    padding: "20px 24px",
    marginBottom: "20px",
    display: "flex",
    gap: "24px",
    flexWrap: "wrap",
  },
  filterCol: { flex: "1 1 220px", minWidth: 200 },
  label: { fontSize: "13px", color: colors.subtext, marginBottom: "6px", display: "block" },
  dateBtn: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 12px",
    borderRadius: "8px",
    border: `1px solid ${colors.inputBorder}`,
    background: "#fff",
    fontSize: "14px",
    color: colors.text,
    cursor: "pointer",
    boxSizing: "border-box",
    position: "relative",
  },
  hiddenDateInput: {
    position: "absolute",
    inset: 0,
    opacity: 0,
    cursor: "pointer",
    width: "100%",
    height: "100%",
  },
  readonlyBox: {
    width: "100%",
    padding: "9px 12px",
    borderRadius: "8px",
    border: `1px solid ${colors.inputBorder}`,
    background: "#f9fafb",
    fontSize: "14px",
    color: colors.text,
    boxSizing: "border-box",
  },
  card: {
    background: colors.card,
    borderRadius: "12px",
    border: `1px solid ${colors.border}`,
    overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    fontSize: "12px",
    fontWeight: 600,
    color: colors.heading,
    padding: "14px 24px",
    borderBottom: `1px solid ${colors.border}`,
  },
  td: { padding: "18px 24px", fontSize: "14px", color: colors.text, borderBottom: `1px solid ${colors.border}` },
  tdBold: { fontWeight: 600 },
  workFromCell: { display: "flex", alignItems: "center", gap: "6px", color: colors.subtext, fontSize: "14px" },
  pill: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 500,
  },
  pillSubmitted: { background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" },
  pillDraft: { background: "#f9fafb", color: "#6b7280", border: "1px solid #e5e7eb" },
  actionsCell: { padding: "18px 24px", borderBottom: `1px solid ${colors.border}`, textAlign: "right" },
  iconBtn: {
    width: "28px", height: "28px", borderRadius: "6px", border: "none", background: "transparent",
    display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", marginLeft: "6px",
  },
  emptyRow: { padding: "40px", textAlign: "center", color: colors.subtext, fontSize: "14px" },
detailTable: { width: "100%", fontSize: "13px", borderCollapse: "collapse", border: `1px solid ${colors.border}` },
  detailTh: {
    textAlign: "left",
    color: "#7c3aed",
    fontWeight: 700,
    fontSize: "11px",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    padding: "10px 12px",
    border: `1px solid ${colors.border}`,
    background: "#f9fafb",
  },
  detailTd: {
    padding: "14px 12px",
    color: colors.text,
    border: `1px solid ${colors.border}`,
    fontSize: "14px",
  },
  workFromPill: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 600,
    background: "#ede9fe",
    color: "#6d28d9",
  },
  timeValue: {
    fontWeight: 700,
    color: "#6d28d9",
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(17, 24, 39, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 99999,
    padding: "20px",
  },
  modalCard: {
    background: colors.card,
    borderRadius: "12px",
    width: "min(1100px, 95vw)",
    maxHeight: "85vh",
    overflowY: "auto",
    boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 24px",
    borderBottom: `1px solid ${colors.border}`,
    position: "sticky",
    top: 0,
    background: colors.card,
    zIndex: 1,
  },
  modalTitle: { fontSize: "16px", fontWeight: 700, color: colors.text, margin: 0 },
  modalCloseBtn: {
    width: "28px", height: "28px", borderRadius: "6px", border: "none", background: "transparent",
    display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
  },
  modalBody: { padding: "20px 24px" },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    padding: "16px 24px",
    borderTop: `1px solid ${colors.border}`,
    position: "sticky",
    bottom: 0,
    background: colors.card,
  },
  editEntryCard: {
    border: `1px solid ${colors.border}`,
    borderRadius: "10px",
    padding: "14px",
    marginBottom: "12px",
    position: "relative",
  },
  editGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    marginBottom: "10px",
  },
  editField: { fontSize: "13px" },
  editLabel: { fontSize: "12px", color: colors.subtext, marginBottom: "4px", display: "block" },
  editInput: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: "6px",
    border: `1px solid ${colors.inputBorder}`,
    fontSize: "13px",
    boxSizing: "border-box",
  },
  removeEntryBtn: {
    position: "absolute",
    top: "10px",
    right: "10px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#dc2626",
  },
  primaryBtn: {
    padding: "9px 18px",
    borderRadius: "8px",
    border: "none",
    background: colors.primary,
    color: "#fff",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryBtn: {
    padding: "9px 18px",
    borderRadius: "8px",
    border: `1px solid ${colors.inputBorder}`,
    background: "#fff",
    color: colors.text,
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },
};

const STATUS_OPTIONS = ["All Statuses", "Submitted", "Draft"];

const workNatureColors = {
  Support: { bg: "#FEF3E2", color: "#C2740C" },
  Testing: { bg: "#E0F2FE", color: "#0369A1" },
  Development: { bg: "#EDE9FE", color: "#6D28D9" },
};

const formatDisplayDate = (iso) => {
  if (!iso) return "Select date";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatRowDate = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const formatHours = (h) => (Number.isInteger(h) ? `${h}h` : `${h.toFixed(1)}h`);

const todayIso = () => new Date().toISOString().slice(0, 10);

const minutesToHHMM = (total) => {
  const t = Math.max(0, total || 0);
  const h = Math.floor(t / 60);
  const m = t % 60;
  return `${h}:${m.toString().padStart(2, "0")}`;
};

const parseTimeToMinutes = (value) => {
  if (!value) return null;
  let v = String(value).trim();
  if (!v.includes(":")) v = v + ":00";
  let [h, m] = v.split(":");
  if (m === "") m = "00";
  if (m.length === 1) m = m + "0";
  const hours = Number(h);
  const minutes = Number(m);
  if (isNaN(hours) || isNaN(minutes) || hours < 0 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
};

const DateField = ({ label, value, onChange, min }) => {
  const ref = useRef(null);
  return (
    <div>
      <label style={styles.label}>{label}</label>
      <div style={styles.dateBtn} onClick={() => ref.current?.showPicker?.()}>
        <Calendar size={15} color={colors.subtext} />
        <span>{formatDisplayDate(value)}</span>
        <input
          ref={ref}
          type="date"
          value={value}
          min={min}
          onChange={(e) => onChange(e.target.value)}
          style={styles.hiddenDateInput}
        />
      </div>
    </div>
  );
};

const EmployeeReport = () => {
  const token = localStorage.getItem("token");
  const userInfoId = Number(localStorage.getItem("id"));
  const employeeName = localStorage.getItem("userName");

  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());
  const [status, setStatus] = useState("All Statuses");
  const [rawEntries, setRawEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const [previewRow, setPreviewRow] = useState(null);

  const [editRow, setEditRow] = useState(null);
  const [editEntries, setEditEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [modules, setModules] = useState([]);
  const [workNatures, setWorkNatures] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    getAllProjectModules(token)
      .then((res) => setModules(res.data.map((m) => ({ id: m.moduleId, name: m.projectModuleName }))))
      .catch(() => setModules([]));
    getAllWorkNatures(token)
      .then((res) => setWorkNatures(res.data.map((w) => ({ id: w.id, name: w.workNatureName }))))
      .catch(() => setWorkNatures([]));
    getAllClients(token)
      .then((res) =>
        setClients((res.data || []).map((i) => ({ id: i.id, clientName: i.instituteName })))
      )
      .catch(() => setClients([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchReports = () => {
    if (!userInfoId) return;
    setLoading(true);
    getMyReports(token, { userInfoId, fromDate, toDate })
      .then((res) => setRawEntries(res.data || []))
      .catch(() => {
        setRawEntries([]);
        toast.error("Failed to load reports");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, userInfoId, token]);

  const groupedRows = useMemo(() => {
    const map = new Map();
    rawEntries.forEach((e) => {
      const key = e.workingDate;
      if (!map.has(key)) {
        map.set(key, { date: key, totalMinutes: 0, totalEntries: 0, workingFrom: e.workingFrom, entries: [] });
      }
      const g = map.get(key);
      g.totalMinutes += e.minutes || 0;
      g.totalEntries += 1;
      g.entries.push(e);
    });
    return Array.from(map.values())
      .map((g) => ({
        ...g,
        totalHours: Math.round((g.totalMinutes / 60) * 10) / 10,
        status: "Submitted",
      }))
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [rawEntries]);

  const filteredRows = useMemo(() => {
    if (status === "All Statuses") return groupedRows;
    return groupedRows.filter((r) => r.status === status);
  }, [groupedRows, status]);

  const handleDownload = (row) => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(14);
      doc.text(`Daily Report — ${formatRowDate(row.date)}`, 14, 16);
      autoTable(doc, {
        startY: 24,
        head: [["Module", "Work Nature", "Details", "Hours"]],
        body: row.entries.map((e) => [
          e.projectModule?.projectModuleName || "-",
          e.workNature?.workNatureName || "-",
          e.workDetails || "-",
          `${Math.round(((e.minutes || 0) / 60) * 100) / 100}h`,
        ]),
        styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak", valign: "top" },
        headStyles: { fontSize: 9, fontStyle: "bold" },
        bodyStyles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 32 },
          1: { cellWidth: 32 },
          2: { cellWidth: "auto" },
          3: { cellWidth: 16, halign: "right" },
        },
      });
      doc.save(`daily-report-${row.date}.pdf`);
      toast.success("Downloaded");
    } catch {
      toast.error("Download failed");
    }
  };
  const handleDownloadAll = () => {
    if (filteredRows.length === 0) {
      toast.error("No reports found for this range");
      return;
    }
    try {
      const doc = new jsPDF();
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 14;

      doc.setFontSize(16);
      doc.setFont(undefined, "bold");
      doc.text(
        `Daily Reports — ${formatRowDate(fromDate)} to ${formatRowDate(toDate)}`,
        margin,
        18
      );
      doc.setFont(undefined, "normal");
      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128);
      doc.text(employeeName || "", margin, 25);
      doc.setTextColor(0, 0, 0);

      let cursorY = 34;

      filteredRows.forEach((row) => {
        // Only force a new page if there isn't reasonable room left for a
        // heading + at least one table row on the current page.
        if (cursorY > pageHeight - 60) {
          doc.addPage();
          cursorY = 20;
        }

        doc.setFontSize(12);
        doc.setFont(undefined, "bold");
        doc.text(formatRowDate(row.date), margin, cursorY);
        doc.setFont(undefined, "normal");
        doc.setFontSize(10);
        doc.setTextColor(107, 114, 128);
        doc.text(
          `Total: ${formatHours(row.totalHours)}  •  ${row.totalEntries} ${
            row.totalEntries === 1 ? "entry" : "entries"
          }`,
          pageWidth - margin,
          cursorY,
          { align: "right" }
        );
        doc.setTextColor(0, 0, 0);

        autoTable(doc, {
          startY: cursorY + 5,
          margin: { left: margin, right: margin },
          head: [["Client", "Module", "Work Nature", "Details", "Hours"]],
          body: row.entries.map((e) => [
            e.institute?.instituteName || "-",
            e.projectModule?.projectModuleName || "-",
            e.workNature?.workNatureName || "-",
            e.workDetails || "-",
            `${Math.round(((e.minutes || 0) / 60) * 100) / 100}h`,
          ]),
          styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak", valign: "top" },
          headStyles: { fillColor: [47, 95, 220], textColor: 255, fontStyle: "bold", fontSize: 9 },
          bodyStyles: { fontSize: 9 },
          alternateRowStyles: { fillColor: [248, 249, 251] },
          columnStyles: {
            0: { cellWidth: 24 },
            1: { cellWidth: 28 },
            2: { cellWidth: 32 },
            3: { cellWidth: "auto" },
            4: { cellWidth: 16, halign: "right" },
          },
        });

        cursorY = doc.lastAutoTable.finalY + 14;
      });

      doc.save(`daily-reports-${fromDate}_to_${toDate}.pdf`);
      toast.success("Downloaded consolidated report");
    } catch {
      toast.error("Download failed");
    }
  };

  const openEditModal = (row) => {
    setEditRow(row);
    setEditEntries(
      row.entries.map((e) => ({
        id: e.id,
        projectModuleId: e.projectModule?.id ? String(e.projectModule.id) : "",
        workNatureId: e.workNature?.id ? String(e.workNature.id) : "",
        clientId: e.institute?.id ? String(e.institute.id) : "",
        workDetails: e.workDetails || "",
        workingFrom: e.workingFrom || "Office",
        timeText: minutesToHHMM(e.minutes || 0),
        dailySummary: e.dailySummary || "",
      }))
    );
  };

  const closeEditModal = () => {
    setEditRow(null);
    setEditEntries([]);
  };

  const updateEditEntry = (index, field, value) => {
    setEditEntries((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry))
    );
  };

  const removeEditEntry = (index) => {
    setEditEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveEdit = async () => {
    if (!editRow) return;

    if (editEntries.length === 0) {
      toast.error("At least one entry is required");
      return;
    }

    const parsedEntries = [];
    for (let i = 0; i < editEntries.length; i++) {
      const e = editEntries[i];
      if (!e.clientId || !e.projectModuleId || !e.workNatureId || !e.workDetails.trim() || !e.timeText.trim()) {
        toast.error(`Entry ${i + 1}: please fill in client, module, work nature, hours, and details`);
        return;
      }
      const minutes = parseTimeToMinutes(e.timeText);
      if (!minutes || minutes <= 0) {
        toast.error(`Entry ${i + 1}: invalid hours (e.g. 2, 2:30, 1:45)`);
        return;
      }
      parsedEntries.push({
        ...(e.id && { id: e.id }),
        projectModuleId: Number(e.projectModuleId),
        workNatureId: Number(e.workNatureId),
        ...(e.clientId && { clientId: Number(e.clientId) }),
        workDetails: e.workDetails.trim(),
        minutes,
        workingFrom: e.workingFrom,
      });
    }

    const totalMinutes = parsedEntries.reduce((sum, e) => sum + e.minutes, 0);
    if (totalMinutes > 8 * 60) {
      toast.error(`Total hours (${minutesToHHMM(totalMinutes)}) exceed the 8:00 daily limit`);
      return;
    }

    const payload = {
      workingDate: editRow.date,
      dailySummary: editEntries[0]?.dailySummary || "",
      reports: parsedEntries,
    };

    setSaving(true);
    try {
      await saveOrUpdateDailyReport(payload, token, userInfoId);
      toast.success("Report updated successfully");
      closeEditModal();
      fetchReports();
    } catch (err) {
      toast.error("Failed to update report");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <div style={styles.page}>
        <h1 style={styles.title}>My Reports</h1>
        <div style={styles.subtitle}>
          Review your submitted and draft daily reports.
        </div>

        <div style={styles.filterCard}>
          <div style={styles.filterCol}>
            <DateField label="From Date" value={fromDate} onChange={setFromDate} />
          </div>
          <div style={styles.filterCol}>
            <DateField label="To Date" value={toDate} onChange={setToDate} min={fromDate} />
          </div>
          <div style={styles.filterCol}>
            <label style={styles.label}>Employee</label>
            <div style={styles.readonlyBox}>{employeeName || "-"}</div>
          </div>
          <div style={{ ...styles.filterCol, display: "flex", alignItems: "flex-end" }}>
            <button
              type="button"
              style={{
                ...styles.primaryBtn,
                padding: "7px 12px",
                fontSize: "12.5px",
                width: "auto",
                opacity: loading || filteredRows.length === 0 ? 0.5 : 1,
                cursor: loading || filteredRows.length === 0 ? "not-allowed" : "pointer",
              }}
              onClick={handleDownloadAll}
              disabled={loading || filteredRows.length === 0}
            >
              <Download size={13} style={{ marginRight: 5, verticalAlign: "-2px" }} />
              Download All
            </button>
          </div>
        </div>

        <div style={styles.card}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Total Hours</th>
                <th style={styles.th}>Total Entries</th>
                <th style={styles.th}>Working From</th>
                <th style={{ ...styles.th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={styles.emptyRow}>Loading...</td></tr>
              ) : filteredRows.length === 0 ? (
                <tr><td colSpan={5} style={styles.emptyRow}>No reports found for this range</td></tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.date}>
                    <td style={{ ...styles.td, ...styles.tdBold }}>{formatRowDate(row.date)}</td>
                    <td style={styles.td}>{formatHours(row.totalHours)}</td>
                    <td style={styles.td}>{row.totalEntries}</td>
                    <td style={styles.td}>
                      <span style={styles.workFromCell}>
                        {row.workingFrom === "Home" ? <HomeIcon size={14} /> : <Building2 size={14} />}
                        {row.workingFrom || "-"}
                      </span>
                    </td>
                    <td style={styles.actionsCell}>
                      <button style={styles.iconBtn} title="View" onClick={() => setPreviewRow(row)}>
                        <Eye size={16} color={colors.iconGray} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {previewRow && createPortal(
          <div style={styles.modalOverlay} onClick={() => setPreviewRow(null)}>
            <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <div>
                  <h3 style={styles.modalTitle}>Work Details</h3>
                  <div style={{ fontSize: 12.5, color: colors.subtext, marginTop: 2 }}>
                    {formatRowDate(previewRow.date)} • {previewRow.totalEntries}{" "}
                    {previewRow.totalEntries === 1 ? "entry" : "entries"} • Total{" "}
                    {formatHours(previewRow.totalHours)}
                  </div>
                </div>
                <button style={styles.modalCloseBtn} onClick={() => setPreviewRow(null)} title="Close">
                  <X size={18} color={colors.iconGray} />
                </button>
              </div>

              <div style={{ ...styles.modalBody, background: "#f9fafb" }}>
                {previewRow.entries.map((e, idx, arr) => {
                  const natureName = e.workNature?.workNatureName || "-";
                  const nc = workNatureColors[natureName] || { bg: "#f3f4f6", color: "#374151" };
                  const fieldLabel = {
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    color: "#7c3aed",
                    marginBottom: 6,
                  };
                  const fieldValue = { fontSize: 14.5, color: colors.text, fontWeight: 500 };

                  return (
                    <div
                      key={e.id ?? idx}
                      style={{
                        background: "#fff",
                        border: `1px solid ${colors.border}`,
                        borderRadius: 10,
                        padding: "16px 20px",
                        marginBottom: idx === arr.length - 1 ? 0 : 14,
                      }}
                    >
                      {/* Header: entry number + time */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          paddingBottom: 12,
                          marginBottom: 14,
                          borderBottom: `1px solid ${colors.border}`,
                        }}
                      >
                        <strong style={{ fontSize: 14 }}>
                          Entry {idx + 1} of {arr.length}
                        </strong>
                        <span
                          style={{
                            background: "#ede9fe",
                            color: "#6d28d9",
                            fontWeight: 700,
                            fontSize: 13,
                            padding: "4px 12px",
                            borderRadius: 999,
                          }}
                        >
                          {minutesToHHMM(e.minutes || 0)} hrs
                        </span>
                      </div>

                      {/* Info grid: From / Client / Module / Work Nature */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                          gap: 18,
                          marginBottom: 16,
                        }}
                      >
                        <div>
                          <div style={fieldLabel}>From</div>
                          <span style={styles.workFromPill}>{e.workingFrom || previewRow.workingFrom || "-"}</span>
                        </div>
                        <div>
                          <div style={fieldLabel}>Client</div>
                          <div style={fieldValue}>{e.institute?.instituteName || "-"}</div>
                        </div>
                        <div>
                          <div style={fieldLabel}>Module</div>
                          <div style={fieldValue}>{e.projectModule?.projectModuleName || "-"}</div>
                        </div>
                        <div>
                          <div style={fieldLabel}>Work Nature</div>
                          <span
                            style={{
                              display: "inline-block",
                              fontWeight: 600,
                              fontSize: 12.5,
                              padding: "4px 10px",
                              borderRadius: 6,
                              background: nc.bg,
                              color: nc.color,
                            }}
                          >
                            {natureName}
                          </span>
                        </div>
                      </div>

                      {/* Task */}
                      <div>
                        <div style={fieldLabel}>Task</div>
                        <div
                          style={{
                            background: "#f9fafb",
                            border: `1px solid ${colors.border}`,
                            borderRadius: 8,
                            padding: "12px 14px",
                            fontSize: 14,
                            lineHeight: 1.6,
                            color: colors.text,
                            whiteSpace: "pre-line",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {e.workDetails || "-"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}

        {editRow && createPortal(
          <div style={styles.modalOverlay} onClick={closeEditModal}>
            <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>Edit Report — {formatRowDate(editRow.date)}</h3>
                <button style={styles.modalCloseBtn} onClick={closeEditModal} title="Close">
                  <X size={18} color={colors.iconGray} />
                </button>
              </div>
              <div style={styles.modalBody}>
                {editEntries.map((entry, index) => (
                  <div key={entry.id || `new-${index}`} style={styles.editEntryCard}>
                    {editEntries.length > 1 && (
                      <button
                        style={styles.removeEntryBtn}
                        title="Remove entry"
                        onClick={() => removeEditEntry(index)}
                        type="button"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                    <div style={styles.editGrid}>
                      <div style={styles.editField}>
                        <label style={styles.editLabel}>Client</label>
                        <select
                          style={styles.editInput}
                          value={entry.clientId}
                          onChange={(e) => updateEditEntry(index, "clientId", e.target.value)}
                        >
                          <option value="">Select client</option>
                          {clients.map((c) => (
                            <option key={c.id} value={String(c.id)}>{c.clientName}</option>
                          ))}
                        </select>
                      </div>
                      <div style={styles.editField}>
                        <label style={styles.editLabel}>Module</label>
                        <select
                          style={styles.editInput}
                          value={entry.projectModuleId}
                          onChange={(e) => updateEditEntry(index, "projectModuleId", e.target.value)}
                        >
                          <option value="">Select module</option>
                          {modules.map((m) => (
                            <option key={m.id} value={String(m.id)}>{m.name}</option>
                          ))}
                        </select>
                      </div>
                      <div style={styles.editField}>
                        <label style={styles.editLabel}>Work Nature</label>
                        <select
                          style={styles.editInput}
                          value={entry.workNatureId}
                          onChange={(e) => updateEditEntry(index, "workNatureId", e.target.value)}
                        >
                          <option value="">Select nature</option>
                          {workNatures.map((w) => (
                            <option key={w.id} value={String(w.id)}>{w.name}</option>
                          ))}
                        </select>
                      </div>
                      <div style={styles.editField}>
                        <label style={styles.editLabel}>Hours (e.g. 2, 2:30)</label>
                        <input
                          style={styles.editInput}
                          type="text"
                          value={entry.timeText}
                          onChange={(e) => updateEditEntry(index, "timeText", e.target.value)}
                        />
                      </div>
                    </div>
                    <div style={styles.editField}>
                      <label style={styles.editLabel}>Working From</label>
                      <select
                        style={{ ...styles.editInput, marginBottom: "10px" }}
                        value={entry.workingFrom}
                        onChange={(e) => updateEditEntry(index, "workingFrom", e.target.value)}
                      >
                        <option value="Office">Office</option>
                        <option value="Home">WFH</option>
                      </select>
                    </div>
                    <div style={styles.editField}>
                      <label style={styles.editLabel}>Work Details</label>
                      <textarea
                        style={{ ...styles.editInput, minHeight: "60px", resize: "vertical" }}
                        value={entry.workDetails}
                        onChange={(e) => updateEditEntry(index, "workDetails", e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div style={styles.modalFooter}>
                <button type="button" style={styles.secondaryBtn} onClick={closeEditModal} disabled={saving}>
                  Cancel
                </button>
                <button type="button" style={styles.primaryBtn} onClick={handleSaveEdit} disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EmployeeReport;
